/**
 * BUBS Naturals - Performance Monitoring
 * Tracks performance metrics and sends to TrackJS
 * 
 * Monitors:
 * - Page load times
 * - Core Web Vitals (LCP, FID, CLS)
 * - Cart API performance
 * - Resource loading
 * - Mobile-specific metrics
 * - User interaction patterns
 */

(function() {
  'use strict';
  
  // Performance monitoring configuration
  const PERF_CONFIG = {
    thresholds: {
      pageLoad: 4000,        // 4 seconds - poor
      domContentLoaded: 1500, // 1.5 seconds - poor
      firstPaint: 2000,      // 2 seconds - poor
      cartLoad: 1000,        // 1 second - poor
      apiCall: 2000,         // 2 seconds - poor
      lcp: 2500,             // 2.5 seconds - poor LCP
      cls: 0.1               // 0.1 - poor CLS
    },
    sampleRate: 1.0,         // Track 100% of sessions (adjust as needed)
    enabled: true,
    debugMode: false,
    trackMetrics: {
      pageLoad: true,
      coreWebVitals: true,
      cartPerformance: true,
      resourceLoading: true,
      userInteractions: true,
      mobileMetrics: true
    }
  };
  
  // Override with custom configuration if available
  if (window.PERFORMANCE_CONFIG) {
    Object.assign(PERF_CONFIG, window.PERFORMANCE_CONFIG);
  }
  
  // Skip if TrackJS not available or sampling
  if (!window.TrackJS || !PERF_CONFIG.enabled || Math.random() > PERF_CONFIG.sampleRate) {
    return;
  }
  
  // Performance data collection
  const perfData = {
    page: window.location.pathname,
    template: window.globalVariables?.template || 'unknown',
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    connection: navigator.connection ? {
      effectiveType: navigator.connection.effectiveType,
      downlink: navigator.connection.downlink,
      rtt: navigator.connection.rtt
    } : null,
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight
    },
    metrics: {},
    sessionStart: performance.now()
  };
  
  // Helper function to track performance events
  function trackPerformance(eventName, value, additionalData = {}) {
    if (value === undefined || value === null) return;
    
    const metricData = {
      metric: eventName,
      value: Math.round(value * 100) / 100,
      page: perfData.page,
      template: perfData.template,
      user_agent: perfData.userAgent,
      connection_type: perfData.connection?.effectiveType || 'unknown',
      viewport: `${perfData.viewport.width}x${perfData.viewport.height}`,
      timestamp: perfData.timestamp,
      ...additionalData
    };
    
    // Always send to metadata for context (not as error)
    if (window.TrackJS && TrackJS.isInstalled) {
      TrackJS.addMetadata({ 
        performance_metrics: metricData 
      });
    }
    
    // Only escalate to error if performance is truly poor
    const threshold = additionalData.threshold_exceeded;
    if (threshold && window.TrackJS && TrackJS.isInstalled) {
      // Get the threshold value that was exceeded
      const thresholdValue = PERF_CONFIG.thresholds[eventName.replace('_time', '').replace('_', '')] || 
                            PERF_CONFIG.thresholds[eventName] || 
                            'unknown';
      
      TrackJS.track(`PERF_${eventName.toUpperCase()}_SLOW`, {
        message: `Poor ${eventName}: ${metricData.value}ms on ${metricData.page} (${metricData.template}) - threshold: ${thresholdValue}ms`,
        page: metricData.page,
        template: metricData.template,
        performance_grade: additionalData.performance_grade,
        threshold_exceeded: thresholdValue,
        threshold_value: thresholdValue,
        metric: metricData
      });
    }
    
  }
  
  // Helper function to check if performance is poor
  function isPoorPerformance(metric, value, threshold) {
    return value > threshold;
  }
  
  // Track page load performance
  function trackPageLoad() {
    if (!PERF_CONFIG.trackMetrics.pageLoad) return;
    
    const loadTime = performance.now();
    perfData.metrics.pageLoad = loadTime;
    
    trackPerformance('page_load_time', loadTime, {
      threshold_exceeded: isPoorPerformance('pageLoad', loadTime, PERF_CONFIG.thresholds.pageLoad),
      performance_grade: loadTime < 1000 ? 'excellent' : 
                        loadTime < 2000 ? 'good' : 
                        loadTime < 4000 ? 'needs_improvement' : 'poor',
      message: `Page loaded in ${loadTime}ms`
    });
  }
  
  // Track Core Web Vitals
  function trackCoreWebVitals() {
    if (!PERF_CONFIG.trackMetrics.coreWebVitals || !performance.getEntriesByType) return;
    
    // Largest Contentful Paint (LCP)
    const lcpEntries = performance.getEntriesByType('largest-contentful-paint');
    if (lcpEntries.length > 0) {
      const lcp = lcpEntries[lcpEntries.length - 1].startTime;
      trackPerformance('largest_contentful_paint', lcp, {
        threshold_exceeded: lcp > PERF_CONFIG.thresholds.lcp,
        performance_grade: lcp < 1200 ? 'excellent' : 
                          lcp < 2500 ? 'good' : 
                          lcp < 4000 ? 'needs_improvement' : 'poor'
      });
    }
    
    // First Paint
    const paintEntries = performance.getEntriesByType('paint');
    paintEntries.forEach(entry => {
      if (entry.name === 'first-paint') {
        trackPerformance('first_paint', entry.startTime, {
          threshold_exceeded: isPoorPerformance('firstPaint', entry.startTime, PERF_CONFIG.thresholds.firstPaint)
        });
      }
    });
  }
  
  // Track DOM Content Loaded
  function trackDOMContentLoaded() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function() {
        const domLoadTime = performance.now();
        trackPerformance('dom_content_loaded', domLoadTime, {
          threshold_exceeded: isPoorPerformance('domContentLoaded', domLoadTime, PERF_CONFIG.thresholds.domContentLoaded)
        });
      });
    } else {
      // Already loaded
      trackPerformance('dom_content_loaded', 0);
    }
  }
  
  // Track resource loading performance
  function trackResourcePerformance() {
    if (!PERF_CONFIG.trackMetrics.resourceLoading || !performance.getEntriesByType) return;
    
    const resources = performance.getEntriesByType('resource');
    let totalResourceSize = 0;
    let slowResources = 0;
    let imageCount = 0;
    let scriptCount = 0;
    let stylesheetCount = 0;
    
    resources.forEach(resource => {
      totalResourceSize += resource.transferSize || 0;
      
      // Count resource types
      switch (resource.initiatorType) {
        case 'img':
          imageCount++;
          break;
        case 'script':
          scriptCount++;
          break;
        case 'link':
          if (resource.name.includes('.css')) {
            stylesheetCount++;
          }
          break;
      }
      
      // Track slow resources (>2 seconds)
      if (resource.duration > 2000) {
        slowResources++;
        trackPerformance('slow_resource', resource.duration, {
          resource_url: resource.name,
          resource_type: resource.initiatorType,
          resource_size: resource.transferSize || 0
        });
      }
    });
    
    // Track total page metrics
    trackPerformance('total_page_size', totalResourceSize, {
      slow_resources_count: slowResources,
      total_resources: resources.length,
      image_count: imageCount,
      script_count: scriptCount,
      stylesheet_count: stylesheetCount
    });
  }
  
  
  // Track cart-related performance
  function trackCartPerformance() {
    if (!PERF_CONFIG.trackMetrics.cartPerformance) return;
    
    const originalFetch = window.fetch;
    window.fetch = function(...args) {
      const startTime = performance.now();
      const url = args[0];
      
      return originalFetch.apply(this, args).then(response => {
        const endTime = performance.now();
        const duration = endTime - startTime;
        
        // Track cart API performance
        if (typeof url === 'string' && url.includes('/cart')) {
          trackPerformance('cart_api_call', duration, {
            api_endpoint: url,
            response_status: response.status,
            threshold_exceeded: duration > PERF_CONFIG.thresholds.cartLoad
          });
        }
        
        // Track slow API calls
        if (duration > PERF_CONFIG.thresholds.apiCall) {
          trackPerformance('slow_api_call', duration, {
            api_endpoint: url,
            response_status: response.status
          });
        }
        
        return response;
      }).catch(error => {
        const endTime = performance.now();
        const duration = endTime - startTime;
        
        trackPerformance('api_call_error', duration, {
          api_endpoint: url,
          error_message: error.message
        });
        
        throw error;
      });
    };
  }
  
  // Track user interaction patterns
  function trackUserInteractions() {
    if (!PERF_CONFIG.trackMetrics.userInteractions) return;
    
    let interactionStart = performance.now();
    let interactionCount = 0;
    
    ['click', 'touchstart', 'keydown'].forEach(eventType => {
      document.addEventListener(eventType, function() {
        interactionCount++;
        const interactionTime = performance.now() - interactionStart;
        
        // Track if user is clicking rapidly (might indicate slow response)
        if (interactionCount > 3 && interactionTime < 1000) {
          trackPerformance('rapid_interactions', interactionCount, {
            time_window: interactionTime,
            event_type: eventType
          });
        }
        
        interactionStart = performance.now();
        interactionCount = 0;
      }, { passive: true });
    });
  }
  
  // Track Cumulative Layout Shift (CLS)
  function trackCumulativeLayoutShift() {
    if (!window.PerformanceObserver) return;
    
    let clsScore = 0;
    let clsEntries = [];
    
    try {
      const clsObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (!entry.hadRecentInput) {
            clsScore += entry.value;
            clsEntries.push({
              value: entry.value,
              sources: entry.sources || []
            });
          }
        }
      });
      
      clsObserver.observe({ entryTypes: ['layout-shift'] });
      
      window.addEventListener('beforeunload', function() {
        if (clsScore > PERF_CONFIG.thresholds.cls) {
          trackPerformance('cumulative_layout_shift', clsScore, {
            threshold_exceeded: true,
            entry_count: clsEntries.length,
            performance_grade: clsScore < 0.05 ? 'excellent' : 
                              clsScore < 0.1 ? 'good' : 
                              clsScore < 0.25 ? 'needs_improvement' : 'poor'
          });
        }
      });
    } catch (e) {
      // CLS not supported in this browser
      if (PERF_CONFIG.debugMode) {
        console.log('CLS tracking not supported:', e.message);
      }
    }
  }
  
  // Track mobile-specific performance
  function trackMobilePerformance() {
    if (!PERF_CONFIG.trackMetrics.mobileMetrics) return;
    
    const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    if (isMobile) {
      window.addEventListener('load', function() {
        trackPerformance('mobile_page_load', performance.now(), {
          device_type: 'mobile',
          screen_density: window.devicePixelRatio,
          orientation: screen.orientation ? screen.orientation.type : 'unknown',
          touch_support: 'ontouchstart' in window
        });
      });
    }
  }
  
  // Track First Input Delay (FID)
  function trackFirstInputDelay() {
    if (!window.PerformanceObserver) return;
    
    try {
      const fidObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          trackPerformance('first_input_delay', entry.processingStart - entry.startTime, {
            event_type: entry.name,
            performance_grade: entry.processingStart - entry.startTime < 100 ? 'excellent' : 
                              entry.processingStart - entry.startTime < 300 ? 'good' : 
                              entry.processingStart - entry.startTime < 500 ? 'needs_improvement' : 'poor'
          });
        }
      });
      
      fidObserver.observe({ entryTypes: ['first-input'] });
    } catch (e) {
      // FID not supported in this browser
      if (PERF_CONFIG.debugMode) {
        console.log('FID tracking not supported:', e.message);
      }
    }
  }
  
  // Track page visibility changes (performance impact)
  function trackVisibilityChanges() {
    let visibilityStart = performance.now();
    
    document.addEventListener('visibilitychange', function() {
      if (document.hidden) {
        const timeVisible = performance.now() - visibilityStart;
        trackPerformance('page_visibility_time', timeVisible, {
          session_duration: timeVisible
        });
      } else {
        visibilityStart = performance.now();
      }
    });
  }
  
  // Initialize performance monitoring
  function initPerformanceMonitoring() {
    // Track page load events
    if (document.readyState === 'complete') {
      trackPageLoad();
      trackCoreWebVitals();
      trackResourcePerformance();
    } else {
      window.addEventListener('load', function() {
        trackPageLoad();
        trackCoreWebVitals();
        trackResourcePerformance();
      });
    }
    
    // Initialize other tracking
    trackDOMContentLoaded();
    trackCartPerformance();
    trackUserInteractions();
    trackCumulativeLayoutShift();
    trackFirstInputDelay();
    trackMobilePerformance();
    trackVisibilityChanges();
    
    if (PERF_CONFIG.debugMode) {
      console.log('🚀 Performance monitoring initialized');
    }
  }
  
  // Start monitoring when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPerformanceMonitoring);
  } else {
    initPerformanceMonitoring();
  }
  
  // Initialize performance monitoring
  if (window.TrackJS && TrackJS.isInstalled) {
    TrackJS.addMetadata({ 
      performance_monitoring: 'initialized',
      timestamp: new Date().toISOString(),
      page: window.location.pathname
    });
    console.log('🚀 Performance monitoring initialized');
  }
  
  // SKIO Defensive Coding - Prevent Google Analytics errors when SKIO is blocked
  function safeGetUserId() {
    try {
      // Check if SKIO handler exists before calling
      if (window.bus && typeof window.bus.request === 'function') {
        return window.bus.request('user:action', 'getUserId');
      }
      return null;
    } catch (error) {
      console.warn('SKIO getUserId not available:', error);
      return null;
    }
  }

  // Override Google Analytics calls to be defensive
  if (window.gtag) {
    const originalGtag = window.gtag;
    window.gtag = function(...args) {
      // If it's a user_id_update and we don't have userId, skip it
      if (args[1] === 'user_id_update' && !safeGetUserId()) {
        console.log('Skipping user_id_update - no userId available (SKIO blocked)');
        return;
      }
      return originalGtag.apply(this, args);
    };
  }

  // Also handle Google Tag Manager dataLayer pushes
  if (window.dataLayer) {
    const originalPush = window.dataLayer.push;
    window.dataLayer.push = function(data) {
      // Check if this is a user_id_update event and we don't have userId
      if (data && data.event === 'user_id_update' && !safeGetUserId()) {
        console.log('Skipping dataLayer user_id_update - no userId available (SKIO blocked)');
        return;
      }
      return originalPush.apply(this, arguments);
    };
  }

  // Export for debugging (optional)
  window.perfMonitoring = {
    config: PERF_CONFIG,
    data: perfData,
    trackPerformance: trackPerformance,
    safeGetUserId: safeGetUserId
  };
  
})();
